import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  Mail,
  Shield,
  User,
  Users,
} from "lucide-react";
import api from "../../src/api";
import { buildPath } from "../../src/lib/routing";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";

type InviteType = "LDAP" | "LOCAL";

interface ValidateResponse {
  valid: boolean;
  eventName?: string;
  type?: InviteType;
  email?: string;
  error?: string;
}

interface InviteAcceptancePortalProps {
  token: string;
}

export function InviteAcceptancePortal({ token }: InviteAcceptancePortalProps) {
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const [validation, setValidation] = useState<ValidateResponse | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localSuccess, setLocalSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isLdapInvite = validation?.type === "LDAP";
  const isLocalInvite = validation?.type === "LOCAL";

  const ruleMinLength = password.length >= 8;
  const ruleLetters = /[A-Za-z]/.test(password);
  const ruleNumbers = /[0-9]/.test(password);
  const passwordMatches = password.length > 0 && password === confirmPassword;
  const rulesPassed = [ruleMinLength, ruleLetters, ruleNumbers].filter(Boolean).length;

  const strength = useMemo(() => {
    if (rulesPassed <= 1) return { label: "Debil", color: "#ef4444", segments: 1 };
    if (rulesPassed === 2) return { label: "Media", color: "#eab308", segments: 2 };
    return { label: "Fuerte", color: "#22c55e", segments: 3 };
  }, [rulesPassed]);

  const canSubmitLocal = useMemo(() => {
    if (settingPassword) return false;
    if (!name.trim()) return false;
    if (!passwordMatches) return false;
    return rulesPassed === 3;
  }, [name, passwordMatches, rulesPassed, settingPassword]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
    try {
      const { data } = await api.get<ValidateResponse>(`/public/invite/validate/${token}`);
      setValidation(data);
      } catch (error: any) {
        setValidation({
          valid: false,
          error: error?.response?.data?.error || "Invitacion expirada o utilizada.",
        });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [token]);

  const handleAcceptLdap = async () => {
    setAccepting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await api.post(`/public/invite/accept/${token}`, {});
      setSuccessMessage("Invitacion aceptada correctamente. Ya puedes iniciar sesion.");
    } catch (error: any) {
      if (error?.response?.status === 429) {
        setErrorMessage("Demasiadas solicitudes. Intenta nuevamente en un minuto.");
      } else {
        setErrorMessage(error?.response?.data?.error || "No fue posible aceptar la invitacion.");
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleSetPassword = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (password !== confirmPassword) {
      setErrorMessage("Las contrasenas no coinciden.");
      return;
    }

    setSettingPassword(true);
    try {
      await api.post(`/public/invite/set-password/${token}`, {
        name: name.trim() || undefined,
        password,
        confirmPassword,
      });
      setLocalSuccess(true);
      setSuccessMessage("Cuenta configurada correctamente. Ya puedes iniciar sesion para acceder al sistema.");
      setPassword("");
      setConfirmPassword("");
      window.setTimeout(() => {
        window.location.assign(buildPath("/login"));
      }, 3000);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        setErrorMessage("Demasiadas solicitudes. Intenta nuevamente en un minuto.");
      } else {
        setErrorMessage(error?.response?.data?.error || "No fue posible configurar la cuenta.");
      }
    } finally {
      setSettingPassword(false);
    }
  };

  const maskEmail = (email?: string): string => {
    if (!email) return "-";
    const [localPart, domain] = email.split("@");
    if (!localPart || !domain) return email;
    return `${localPart.charAt(0)}***@${domain}`;
  };

  const RuleItem = ({ ok, text }: { ok: boolean; text: string }) => (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-[#22c55e]" : "text-slate-400"}`}>
      {ok ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
      <span>{text}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full max-w-[600px] mx-auto px-2 sm:px-0">
        <Card className="w-full max-w-[600px] rounded-[10px] shadow-lg bg-white">
          <CardContent className="py-12 text-center text-gray-600">Validando invitacion...</CardContent>
        </Card>
      </div>
    );
  }

  if (!validation?.valid) {
    return (
      <div className="w-full max-w-[600px] mx-auto px-2 sm:px-0">
        <Card className="w-full max-w-[600px] rounded-[10px] shadow-lg bg-white">
          <div className="bg-[#1e3a5f] px-6 py-4 rounded-t-[10px] flex items-center gap-3">
            <img src={`${import.meta.env.BASE_URL}escudo-icono.svg`} alt="UNACH" className="h-9 w-auto filter brightness-0 invert" />
            <span className="text-white font-semibold text-sm">Sistema de Congresos UNACH</span>
          </div>
          <CardContent className="p-6 text-sm text-red-700">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Invitacion no valida</h2>
            {validation?.error || "La invitacion expiro o ya fue utilizada."}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[600px] mx-auto px-2 sm:px-0">
      <Card className="w-full max-w-[600px] rounded-[10px] shadow-lg overflow-hidden bg-white">
        <div className="bg-[#1e3a5f] px-6 py-4 flex items-center gap-3">
          <img src={`${import.meta.env.BASE_URL}escudo-icono.svg`} alt="UNACH" className="h-10 w-auto filter brightness-0 invert" />
          <span className="text-white font-semibold text-sm sm:text-base">Sistema de Congresos UNACH</span>
        </div>

        <CardContent className="p-5 sm:p-6 space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]">Invitacion a Comite Evaluador</h1>
            <p className="text-sm text-slate-500 mt-1">
              Has sido invitado a participar como evaluador en un evento academico.
            </p>
          </div>

          <div className="bg-[#eef2f7] rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <Calendar className="h-4 w-4 text-[#1e3a5f] mt-0.5" />
              <span className="font-semibold min-w-20">Evento:</span>
              <span>{validation.eventName || "Evento"}</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <User className="h-4 w-4 text-[#1e3a5f] mt-0.5" />
              <span className="font-semibold min-w-20">Rol:</span>
              <span>Evaluador de Ponencias</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <Mail className="h-4 w-4 text-[#1e3a5f] mt-0.5" />
              <span className="font-semibold min-w-20">Correo:</span>
              <span>{maskEmail(validation.email)}</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <Shield className="h-4 w-4 text-[#1e3a5f] mt-0.5" />
              <span className="font-semibold min-w-20">Acceso:</span>
              <span>{validation.type}</span>
            </div>
          </div>

          {errorMessage ? <p className="text-sm text-[#ef4444]">{errorMessage}</p> : null}
          {successMessage ? <p className="text-sm text-[#22c55e]">{successMessage}</p> : null}

          {isLdapInvite ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Users className="h-4 w-4 text-[#1e3a5f]" />
                Tu cuenta usa autenticacion LDAP institucional.
              </div>
              <Button
                className="w-full py-3 rounded-lg font-semibold bg-[#c9a227] hover:bg-[#b08d1f] text-white"
                onClick={handleAcceptLdap}
                disabled={accepting || Boolean(successMessage)}
              >
                {accepting ? "Aceptando..." : "Aceptar invitacion"}
              </Button>
            </div>
          ) : null}

          {isLocalInvite && !localSuccess ? (
            <form className="space-y-4" onSubmit={handleSetPassword}>
              <h2 className="text-lg font-semibold text-[#1e3a5f]">Configuracion de Cuenta</h2>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Nombre completo <span className="text-[#ef4444]">*</span>
                </label>
                <Input
                  placeholder="Ej: Juan Perez Garcia"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Contrasena <span className="text-[#ef4444]">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Escribe tu contrasena"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Confirmar contrasena <span className="text-[#ef4444]">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Repite tu contrasena"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              {confirmPassword.length > 0 && !passwordMatches ? (
                <p className="text-xs text-[#ef4444]">Las contrasenas no coinciden.</p>
              ) : null}

              <div className="space-y-2">
                <RuleItem ok={ruleMinLength} text="Minimo 8 caracteres" />
                <RuleItem ok={ruleLetters} text="Contiene letras" />
                <RuleItem ok={ruleNumbers} text="Contiene numeros" />
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-1.5">
                  {[1, 2, 3].map((segment) => (
                    <div
                      key={segment}
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: segment <= strength.segments ? strength.color : "#e2e8f0",
                      }}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Fortaleza: <span style={{ color: strength.color }} className="font-semibold">{strength.label}</span>
                </p>
              </div>

              <Button
                type="submit"
                className="w-full py-3 rounded-lg font-semibold bg-[#c9a227] hover:bg-[#b08d1f] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canSubmitLocal || Boolean(successMessage)}
              >
                {settingPassword ? "Configurando..." : "Configurar cuenta y aceptar invitacion"}
              </Button>
            </form>
          ) : null}

          {isLocalInvite && localSuccess ? (
            <div className="space-y-4 rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5 mt-0.5" />
                <div>
                  <p className="font-semibold">Cuenta configurada correctamente</p>
                  <p className="text-sm">Ya puedes iniciar sesión para acceder al sistema.</p>
                </div>
              </div>
              <Button
                type="button"
                className="w-full py-3 rounded-lg font-semibold bg-[#c9a227] hover:bg-[#b08d1f] text-white"
                onClick={() => window.location.assign(buildPath("/login"))}
              >
                Ir a iniciar sesión
              </Button>
            </div>
          ) : null}

          <p className="text-xs text-slate-400 text-center mt-4">
            Esta invitacion fue enviada por la Coordinacion del Comite Cientifico del evento.
            Si no esperabas esta invitacion, puedes ignorar este mensaje.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
